import { useQuery, type QueryClient } from '@tanstack/react-query';
import { getRunActivityLog, getRunRawCalls } from '../../api/controlTestRuns.js';
import { queryKeys } from '../../queryKeys.ts';

/** The saved activity log of a test run; `data` is null when none was recorded. Loaded only while `enabled`. */
export function useRunActivityLog(runId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.controlTesting.runActivityLog(runId ?? ''),
    queryFn: () => getRunActivityLog(runId as string),
    enabled: Boolean(runId) && enabled,
    staleTime: 30_000,
    retry: false,
  });
}

/** The recorded text of a run's model calls; loaded only once `enabled` (the user asked to see it). */
export function useRunRawCalls(runId: string | null | undefined, enabled = false) {
  return useQuery({
    queryKey: queryKeys.controlTesting.runRawCalls(runId ?? ''),
    queryFn: () => getRunRawCalls(runId as string),
    enabled: Boolean(runId) && enabled,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Drop the cached activity log and raw calls of a run. Call it when the run is executed again: the same run
 * keeps its id, and the new log replaces the old one, but a cached copy would be shown for up to a minute.
 */
export function invalidateRunLogQueries(queryClient: QueryClient, runId: string | null | undefined) {
  if (!runId) return Promise.resolve();
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.controlTesting.runActivityLog(runId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.controlTesting.runRawCalls(runId) }),
  ]);
}
